import { Article, Style } from '@/constants';
import type { Db } from '@shared/storage';

/**
 * Every style id an article points at directly -- mirroring exactly where
 * the article renderer (`lib/article-renderer/article-style-builder.ts`)
 * reads style ids from:
 *
 * - `article.style` -- the article's base style (`loadStylesFromArticle`).
 * - `component.styles` -- an array of style ids on a component. Today the
 *   renderer resolves it on `columns` (`calculateInlineStyle`) and `image`
 *   components (`processImageComponentInlineStyles`); collected from *any*
 *   component here, so a component type that starts using it later is
 *   still protected. Over-collecting only ever keeps a style, never
 *   deletes one.
 * - `columns` nest further components (an array of columns, each an array
 *   of components), walked to any depth.
 *
 * `component.style` is deliberately *not* collected: it's an inline style
 * object (dropcap/background properties), not a style id.
 *
 * Ids are stringified -- they're `string` on the wire but arrive as numbers
 * often enough that the `styles` table normalizes every key to a string.
 */
export function collectArticleStyleRefs(
	articles: Array<Article | null | undefined>,
): Set<string> {
	const refs = new Set<string>();
	const add = (id: unknown) => {
		if (id === undefined || id === null || id === '') {
			return;
		}
		if (typeof id === 'string' || typeof id === 'number') {
			refs.add(`${id}`);
		}
	};
	const walk = (components: unknown) => {
		if (!Array.isArray(components)) {
			return;
		}
		for (const component of components) {
			if (!component || typeof component !== 'object') {
				continue;
			}
			const { styles, columns } = component as {
				styles?: unknown;
				columns?: unknown;
			};
			if (Array.isArray(styles)) {
				styles.forEach(add);
			}
			if (Array.isArray(columns)) {
				columns.forEach(walk);
			}
		}
	};

	for (const article of articles) {
		if (!article) {
			continue;
		}
		add(article.style);
		walk(article.components);
	}
	return refs;
}

/**
 * `ids` plus every style they lead to through `desktop`/`tablet`
 * (breakpoint variants the renderer emits alongside the base style) and
 * `parent` (inheritance), resolved against the stored `styles` table.
 * Breadth-first with the result doubling as the visited set, so an
 * author-made cycle (a -> b -> a) terminates. Ids that aren't stored are
 * kept in the result (they're still "referenced") but can't be followed.
 */
export async function expandStyleChain(
	db: Db,
	ids: Iterable<string>,
): Promise<Set<string>> {
	const result = new Set<string>();
	let frontier = [...ids].map((id) => `${id}`);
	while (frontier.length) {
		const next = frontier.filter((id) => !result.has(id));
		if (!next.length) {
			break;
		}
		next.forEach((id) => result.add(id));
		const styles: Array<Style> = await db.getStylesByIds(next);
		frontier = styles.flatMap((style) =>
			[style.desktop, style.tablet, style.parent]
				.filter((id) => id !== undefined && id !== null && id !== '')
				.map((id) => `${id}`),
		);
	}
	return result;
}

/** Every style a set of stored articles needs, following style chains. */
export async function collectArticleStyleIds(
	db: Db,
	articles: Array<Article | null | undefined>,
): Promise<Set<string>> {
	return expandStyleChain(db, collectArticleStyleRefs(articles));
}
